import { Button } from '@/components/atoms';
import { Typography } from '@/components/atoms/Typography';
import { DirhamIcon, DollarReceiptIcon } from '@/components/atoms/icons';
import { UI_PRIMITIVES } from '@/core/constants/uiColors';
import { useUIColors } from '@/core/hooks';
import { useI18nContext } from '@/core/providers/I18nProvider';
import { hasNumericValue } from '@/core/utils/fieldAvailability';
import { isSmallScreen } from '@/core/utils/responsive';
import React from 'react';
import { Platform, StyleSheet, View, type StyleProp, type TextStyle } from 'react-native';
import type { UnitDetailsLabels } from '../constants/getPropertyLabels';
import { usePropertyLabels } from '../hooks/usePropertyLabels';

export interface UnitPriceSummaryProps {
  totalPrice?: number | undefined;
  /** ADHA sanctioned Iskan loan amount in AED (including 0 when API has none). */
  preApprovalAmount?: number;
  /**
   * When true, render only the price card (no "Unit Price" header).
   * Used on mobile where the card sits standalone between the title and
   * the About Unit accordion.
   */
  standalone?: boolean;
  onPayNow?: (() => void) | undefined;
  /**
   * Outstanding balance and Pay now render only after this unit is reserved.
   * A shortfall alone keeps the price and Iskan loan, without that row.
   */
  isUnitReserved?: boolean;
}

const OUTSTANDING_LABEL_COLOR = '#545F73';

type PriceColors = ReturnType<typeof useUIColors>;

function DirhamAmount({
  amount,
  color,
  iconSize,
  variant,
  textStyle,
  avoidClip = false,
}: Readonly<{
  amount: number;
  color: string;
  iconSize: number;
  variant: 'title3' | 'subhead';
  textStyle?: StyleProp<TextStyle> | undefined;
  /** Web Arabic: don't ellipsize the amount when the column is narrow. */
  avoidClip?: boolean;
}>) {
  return (
    <View style={[styles.amountRow, avoidClip ? styles.amountRowWebRtl : null]}>
      <DirhamIcon size={iconSize} color={color} />
      <Typography
        variant={variant}
        color={color}
        {...(avoidClip ? {} : { numberOfLines: 1 })}
        style={textStyle}
      >
        {amount.toLocaleString()}
      </Typography>
    </View>
  );
}

function PriceMetric({
  label,
  amount,
  labelVariant,
  amountColor,
  amountVariant,
  iconSize,
  labelColor = 'secondary',
  labelStyle,
  amountTextStyle,
  avoidClip = false,
}: Readonly<{
  label: string;
  amount: number;
  labelVariant: 'caption' | 'subhead';
  amountColor: string;
  amountVariant: 'title3' | 'subhead';
  iconSize: number;
  labelColor?: string;
  labelStyle?: StyleProp<TextStyle> | undefined;
  amountTextStyle?: StyleProp<TextStyle> | undefined;
  avoidClip?: boolean;
}>) {
  return (
    <View style={[styles.priceColumn, avoidClip ? styles.priceColumnWebRtl : null]}>
      <Typography
        variant={labelVariant}
        color={labelColor}
        style={labelStyle}
      >
        {label}
      </Typography>
      <DirhamAmount
        amount={amount}
        color={amountColor}
        iconSize={iconSize}
        variant={amountVariant}
        textStyle={amountTextStyle}
        avoidClip={avoidClip}
      />
    </View>
  );
}

function IskanCoversColumn({
  amount,
  labels,
  isRTL,
  amountColor,
  avoidClip = false,
}: Readonly<{
  amount: number;
  labels: UnitDetailsLabels;
  isRTL: boolean;
  amountColor: string;
  avoidClip?: boolean;
}>) {
  return (
    <View style={[styles.preApprovalColumn, avoidClip ? styles.columnWebRtl : null]}>
      <Typography
        variant="caption"
        color="highContrast"
        style={styles.preApprovalText}
      >
        {labels.ISKAN_LOAN_TITLE}
      </Typography>
      <View style={styles.preApprovalValue}>
        <View style={styles.preApprovalAmountRow}>
          <DirhamIcon size={14} color={amountColor} />
          <Typography
            variant="subhead"
            color={amountColor}
            {...(avoidClip ? {} : { numberOfLines: 1 })}
          >
            {amount.toLocaleString()}
          </Typography>
          <Typography
            variant="subhead"
            color={amountColor}
            {...(avoidClip ? {} : { numberOfLines: 1 })}
          >
            {labels.ISKAN_LOAN_COVERS_VERB}
          </Typography>
        </View>
        <Typography
          variant="subhead"
          color={amountColor}
          style={[
            styles.preApprovalText,
            Platform.OS === 'web' && isRTL && { textAlign: 'right' },
          ]}
        >
          {labels.ISKAN_LOAN_COVERS_REST}
        </Typography>
      </View>
    </View>
  );
}

function IskanLoanAmountColumn({
  amount,
  amountColor,
  labels,
  labelVariant,
  avoidClip = false,
}: Readonly<{
  amount: number;
  amountColor: string;
  labels: UnitDetailsLabels;
  labelVariant: 'caption' | 'subhead';
  avoidClip?: boolean;
}>) {
  return (
    <View style={[styles.preApprovalColumn, avoidClip ? styles.columnWebRtl : null]}>
      <PriceMetric
        label={labels.ISKAN_LOAN_TITLE}
        amount={amount}
        labelVariant={labelVariant}
        amountColor={amountColor}
        amountVariant="title3"
        iconSize={21}
        avoidClip={avoidClip}
      />
    </View>
  );
}

function IskanShortfallColumn({
  amount,
  amountColor,
  labels,
  labelVariant,
  onPayNow,
  avoidClip = false,
}: Readonly<{
  amount: number;
  amountColor: string;
  labels: UnitDetailsLabels;
  labelVariant: 'caption' | 'subhead';
  onPayNow?: (() => void) | undefined;
  avoidClip?: boolean;
}>) {
  return (
    <View style={[styles.shortfallLoanColumn, avoidClip ? styles.columnWebRtl : null]}>
      <PriceMetric
        label={labels.ISKAN_LOAN_TITLE}
        amount={amount}
        labelVariant={labelVariant}
        amountColor={amountColor}
        amountVariant="title3"
        iconSize={21}
        avoidClip={avoidClip}
      />
      <Button
        title={labels.PAY_NOW}
        variant="primary"
        onPress={onPayNow}
        style={styles.payNowButton}
        textStyle={styles.payNowLabel}
      />
    </View>
  );
}

function PriceMetricsColumn({
  colors,
  showOutstanding,
  hasTotalPrice,
  labels,
  outstandingBalance,
  priceLabelVariant,
  totalPrice,
  avoidClip = false,
}: Readonly<{
  colors: PriceColors;
  showOutstanding: boolean;
  hasTotalPrice: boolean;
  labels: UnitDetailsLabels;
  outstandingBalance: number;
  priceLabelVariant: 'caption' | 'subhead';
  totalPrice: number;
  avoidClip?: boolean;
}>) {
  return (
    <View
      style={[
        styles.metricsColumn,
        showOutstanding && styles.metricsColumnShortfall,
        avoidClip ? styles.columnWebRtl : null,
      ]}
    >
      {hasTotalPrice ? (
        <PriceMetric
          label={labels.TOTAL_PRICE}
          amount={totalPrice}
          labelVariant={priceLabelVariant}
          amountColor={colors.fontPrimary}
          amountVariant="title3"
          iconSize={21}
          avoidClip={avoidClip}
        />
      ) : null}
      {showOutstanding ? (
        <PriceMetric
          label={labels.OUTSTANDING_BALANCE}
          amount={outstandingBalance}
          labelVariant="subhead"
          labelColor={OUTSTANDING_LABEL_COLOR}
          labelStyle={[styles.outstandingLabel, avoidClip ? styles.outstandingLabelWebRtl : null]}
          amountColor={UI_PRIMITIVES.redDefault}
          amountVariant="title3"
          amountTextStyle={styles.outstandingAmount}
          iconSize={18}
          avoidClip={avoidClip}
        />
      ) : null}
    </View>
  );
}

function IskanLoanSection({
  amount,
  amountColor,
  hasIskanLoan,
  hasShortfall,
  showPaymentRow,
  isRTL,
  labels,
  onPayNow,
  priceLabelVariant,
  avoidClip = false,
}: Readonly<{
  amount: number;
  amountColor: string;
  hasIskanLoan: boolean;
  hasShortfall: boolean;
  showPaymentRow: boolean;
  isRTL: boolean;
  labels: UnitDetailsLabels;
  onPayNow?: (() => void) | undefined;
  priceLabelVariant: 'caption' | 'subhead';
  avoidClip?: boolean;
}>) {
  if (!hasIskanLoan) {
    return null;
  }
  if (showPaymentRow) {
    return (
      <IskanShortfallColumn
        amount={amount}
        amountColor={amountColor}
        labels={labels}
        labelVariant={priceLabelVariant}
        onPayNow={onPayNow}
        avoidClip={avoidClip}
      />
    );
  }
  if (hasShortfall) {
    return (
      <IskanLoanAmountColumn
        amount={amount}
        amountColor={amountColor}
        labels={labels}
        labelVariant={priceLabelVariant}
        avoidClip={avoidClip}
      />
    );
  }
  return (
    <IskanCoversColumn
      amount={amount}
      labels={labels}
      isRTL={isRTL}
      amountColor={amountColor}
      avoidClip={avoidClip}
    />
  );
}

/**
 * "Unit Price" section: dollar-receipt header (Figma) + dirham amount prefix.
 * When the unit is reserved and the Iskan loan is below the price, show
 * outstanding balance + Pay Now.
 */
export function UnitPriceSummary({
  totalPrice,
  preApprovalAmount,
  standalone = false,
  onPayNow,
  isUnitReserved = false,
}: Readonly<UnitPriceSummaryProps>) {
  const colors = useUIColors();
  const { isRTL } = useI18nContext();
  const isMobile = isSmallScreen();
  const { UNIT_DETAILS: labels } = usePropertyLabels();
  const showHeader = !standalone;
  const hasTotalPrice = hasNumericValue(totalPrice);
  const hasIskanLoan =
    typeof preApprovalAmount === 'number' && Number.isFinite(preApprovalAmount);
  const loanAmount = hasIskanLoan ? preApprovalAmount : 0;
  const hasShortfall = hasTotalPrice && hasIskanLoan && loanAmount < totalPrice;
  const showPaymentRow = hasShortfall && isUnitReserved;
  const outstandingBalance = showPaymentRow
    ? Math.max(0, totalPrice - loanAmount)
    : 0;
  const priceLabelVariant = isMobile ? 'caption' : 'subhead';
  const iskanAmountColor = colors.fontPrimary;
  const avoidClip = Platform.OS === 'web' && isRTL;

  if (!hasTotalPrice && !hasIskanLoan) {
    return null;
  }

  return (
    <View style={styles.container}>
      {showHeader ? (
        <View style={styles.headerRow}>
          <DollarReceiptIcon size={24} color={colors.fontPrimary} />
          <Typography variant="title3" color="primary">
            {labels.UNIT_PRICE}
          </Typography>
        </View>
      ) : null}

      <View
        style={[
          styles.priceRow,
          isMobile && !showPaymentRow && styles.priceRowMobile,
          showPaymentRow && styles.priceRowShortfall,
          avoidClip && styles.priceRowWebRtl,
          {
            backgroundColor: colors.backgroundLevel1,
            borderColor: colors.outlineDisabled,
          },
        ]}
      >
        <PriceMetricsColumn
          colors={colors}
          showOutstanding={showPaymentRow}
          hasTotalPrice={hasTotalPrice}
          labels={labels}
          outstandingBalance={outstandingBalance}
          priceLabelVariant={priceLabelVariant}
          totalPrice={totalPrice ?? 0}
          avoidClip={avoidClip}
        />

        <IskanLoanSection
          amount={loanAmount}
          amountColor={iskanAmountColor}
          hasIskanLoan={hasIskanLoan}
          hasShortfall={hasShortfall}
          showPaymentRow={showPaymentRow}
          isRTL={isRTL}
          labels={labels}
          onPayNow={onPayNow}
          priceLabelVariant={priceLabelVariant}
          avoidClip={avoidClip}
        />
      </View>
    </View>
  );
}

const styles = StyleSheet.create({
  container: {
    gap: 12,
  },
  headerRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
    paddingTop: 16,
    paddingHorizontal: 12,
  },
  priceRow: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    gap: 12,
    borderWidth: 0.5,
    borderRadius: 12,
    padding: 16,
    minHeight: 88,
    overflow: 'hidden',
    shadowColor: '#000000',
    shadowOffset: { width: 0, height: 0 },
    shadowOpacity: 0.04,
    shadowRadius: 54,
  },
  /** Mobile — drop Total Price so it lines up with the wrapping Iskan block */
  priceRowMobile: {
    alignItems: 'center',
  },
  /**
   * Shortfall adds Outstanding + Pay Now — grow past the single-row 88px
   * minHeight so content never clips or collapses the card.
   */
  priceRowShortfall: {
    alignItems: 'stretch',
    minHeight: 140,
  },
  /**
   * Web Arabic: same Contentful labels at every width. Columns wrap onto
   * their own row instead of being squeezed until the other label disappears.
   */
  priceRowWebRtl: {
    overflow: 'visible',
    alignItems: 'stretch',
    flexWrap: 'wrap',
  },
  columnWebRtl: {
    flexGrow: 1,
    flexShrink: 1,
    flexBasis: '46%',
    minWidth: 220,
    marginLeft: 0,
  },
  metricsColumn: {
    gap: 12,
    flex: 1,
    flexShrink: 1,
    minWidth: 0,
  },
  /** Space between Total Price and Outstanding Balance. */
  metricsColumnShortfall: {
    gap: 16,
  },
  priceColumn: {
    gap: 8,
    flexShrink: 1,
    minWidth: 0,
  },
  priceColumnWebRtl: {
    flexShrink: 0,
    minWidth: 120,
    alignSelf: 'stretch',
  },
  amountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    flexShrink: 1,
    minWidth: 0,
  },
  amountRowWebRtl: {
    flexShrink: 0,
    alignSelf: 'flex-start',
  },
  /** Trailing column — left-align title with currency (desktop + mobile) */
  preApprovalColumn: {
    flexShrink: 0,
    marginLeft: 'auto',
    gap: 4,
    alignItems: 'flex-start',
    maxWidth: '55%',
  },
  shortfallLoanColumn: {
    flexShrink: 0,
    marginLeft: 'auto',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    alignSelf: 'stretch',
    gap: 16,
    minWidth: 120,
  },
  preApprovalValue: {
    alignItems: 'flex-start',
    gap: 2,
  },
  /** Amount + “covers” stay on one line */
  preApprovalAmountRow: {
    flexDirection: 'row',
    flexWrap: 'nowrap',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 4,
  },
  preApprovalText: {},
  /** Figma Font/Size/XS Medium, LH 100%, #545F73 */
  outstandingLabel: {
    fontFamily: 'Matter-Medium',
    fontWeight: '500',
    fontSize: 14,
    lineHeight: 14,
  },
  outstandingLabelWebRtl: {
    lineHeight: 22,
  },
  /** Figma Font/Size/L Medium, LH 100%, Error #D8181E */
  outstandingAmount: {
    fontFamily: 'Matter-Medium',
    fontWeight: '500',
    fontSize: 20,
    lineHeight: 20,
  },
  /** Figma: 139×41, radius Spacing/1 (4), padding Spacing/2 (8) */
  payNowButton: {
    width: 139,
    height: 41,
    paddingHorizontal: 8,
    paddingVertical: 8,
    borderRadius: 4,
    alignSelf: 'flex-start',
  },
  /** Figma Font/Size/Base SemiBold, LH 100%, center — web weight 400 avoids synthetic bold blur */
  payNowLabel: {
    fontFamily: 'Matter-SemiBold',
    fontWeight: Platform.OS === 'web' ? '400' : '600',
    fontSize: 16,
    lineHeight: 16,
    textAlign: 'center',
    ...(Platform.OS === 'web'
      ? ({
          WebkitFontSmoothing: 'antialiased',
          MozOsxFontSmoothing: 'grayscale',
        } as object)
      : null),
  },
});

export default UnitPriceSummary;
